import { BooLogo } from "@/components/BooLogo";
import { FileDropOverlay } from "@/components/FileDropOverlay";
import { MessageMarkdown } from "@/components/MessageMarkdown";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { usePageFileDrop } from "@/hooks/usePageFileDrop";
import { api, ApiError, type BrainChatDecision, type BrainChatMessage, type BrainChatThread } from "@/lib/api";
import { formatCost, relativeTime } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { AudioLines, Check, ChevronRight, Loader2, Paperclip, Send, ShieldAlert, Square, SquarePen, X } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";

// Voice mode pulls in the in-browser speech detector (onnxruntime + Silero
// VAD, ~120 KB gzipped of JS) — loaded only when someone opens it, so the
// dashboard's main bundle stays the same for everyone else.
const VoiceMode = lazy(() => import("./VoiceMode").then((m) => ({ default: m.VoiceMode })));

const MAX_ATTACHMENTS = 8;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const formatBytes = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

// Dashboard-chat keys we mint are namespaced so they read distinctly next to
// backend-initiated threads (e.g. brand-rewrite-<id>) in the same list.
const newThreadKey = () => `dash-${crypto.randomUUID().slice(0, 8)}`;

// An approval prompt from the brain: the tool calls it wants to make and why,
// with Allow all / Deny all. Once decided, the row shows the outcome.
function EscalationCard({
  message,
  onDecide,
}: {
  message: BrainChatMessage;
  onDecide: (escalationId: string, decision: BrainChatDecision) => Promise<unknown>;
}) {
  const esc = message.escalation!;
  const [busy, setBusy] = useState<"approve_all" | "deny_all" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = esc.status === "pending";
  const decide = async (decision: "approve_all" | "deny_all") => {
    setBusy(decision);
    setError(null);
    try {
      await onDecide(esc.escalationId, decision);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't record that decision.");
    } finally {
      setBusy(null);
    }
  };
  const outcome =
    esc.status === "approved" ? "Approved" : esc.status === "denied" ? "Denied" : esc.status === "mixed" ? "Partly approved" : null;
  return (
    <div className="flex justify-start">
      <div className={`max-w-[80%] rounded-lg border px-4 py-3 text-sm ${pending ? "border-amber-400/60 bg-amber-50 dark:bg-amber-950/30" : "bg-muted"}`}>
        <div className="flex items-center gap-2 font-medium">
          <ShieldAlert className="size-4 shrink-0" />
          {pending ? "Boo needs your approval" : `${outcome}${esc.resolvedBy ? ` by ${esc.resolvedBy}` : ""}`}
        </div>
        <ul className="mt-2 space-y-1">
          {esc.toolCalls.map((call) => (
            <li key={call.toolCallId}>
              <code className="rounded bg-background/70 px-1 py-0.5 text-xs">{call.toolName}</code>
              {call.reason && <span className="ml-2 text-muted-foreground">{call.reason}</span>}
            </li>
          ))}
        </ul>
        {pending && (
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" className="gap-1" disabled={busy !== null} onClick={() => void decide("approve_all")}>
              {busy === "approve_all" ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
              Allow all
            </Button>
            <Button size="sm" variant="outline" className="gap-1" disabled={busy !== null} onClick={() => void decide("deny_all")}>
              {busy === "deny_all" ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
              Deny all
            </Button>
          </div>
        )}
        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      </div>
    </div>
  );
}

function CreditsNotice({ message }: { message: BrainChatMessage }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
        <div className="font-medium">Stopped: this Boo is out of credits.</div>
        <a className="mt-1 inline-block underline" href={message.credits?.topupUrl} target="_blank" rel="noreferrer">
          Top up to continue
        </a>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onDecide,
}: {
  message: BrainChatMessage;
  onDecide: (escalationId: string, decision: BrainChatDecision) => Promise<unknown>;
}) {
  if (message.kind === "escalation" && message.escalation) return <EscalationCard message={message} onDecide={onDecide} />;
  if (message.kind === "credits" && message.credits) return <CreditsNotice message={message} />;
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-2 text-sm ${
          isUser ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-muted"
        }`}
      >
        {isUser ? message.text : <MessageMarkdown text={message.text} />}
        {message.attachments && message.attachments.length > 0 && (
          <div className={`mt-2 flex flex-wrap gap-1.5 ${message.text ? "" : "-mt-0"}`}>
            {message.attachments.map((a, i) => (
              <span key={`${a.name}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-background/20 px-2 py-0.5 text-xs" title={`${a.mimeType} · ${formatBytes(a.size)}`}>
                <Paperclip className="size-3" />
                {a.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationList({
  threads,
  costs,
  isLoading,
  activeKey,
  onSelect,
  onNew,
}: {
  threads: BrainChatThread[];
  costs: Record<string, number>;
  isLoading: boolean;
  activeKey: string;
  onSelect: (threadKey: string) => void;
  onNew: () => void;
}) {
  // Sum across every dashboard_chat session the brain has billed — including
  // system sessions (e.g. brand-rewrite) that have no live transcript here.
  const totalSpend = Object.values(costs).reduce((a, b) => a + b, 0);
  return (
    <aside className="w-60 shrink-0 flex flex-col rounded-lg bg-sidebar text-sidebar-foreground ring-1 ring-sidebar-border shadow-sm overflow-hidden">
      <div className="p-3">
        <Button variant="outline" size="sm" className="w-full justify-center gap-2" onClick={onNew}>
          <SquarePen className="size-4" />
          New conversation
        </Button>
      </div>
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" /> Loading…
          </div>
        ) : threads.length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">No conversations yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5 px-1.5 pb-2">
            {threads.map((t) => {
              const isActive = t.threadKey === activeKey;
              const cost = costs[t.threadKey];
              return (
                <li
                  key={t.threadKey}
                  className={`group flex items-center gap-2 rounded-md px-3 py-2 cursor-pointer transition-colors ${
                    isActive ? "bg-foreground/10" : "bg-background hover:bg-muted/60"
                  }`}
                  onClick={() => onSelect(t.threadKey)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                      {t.lastMessageAt > 0 && <span>{relativeTime(t.lastMessageAt)}</span>}
                      {!t.interactive && <span title="Started by the platform, not from here">system</span>}
                      {cost != null && cost > 0 && (
                        <>
                          <span aria-hidden>·</span>
                          <span title="Total spend on this session (LLM + services)">{formatCost(cost)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {isActive && <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {totalSpend > 0 && (
        <div className="flex items-center justify-between border-t border-sidebar-border px-3 py-2 text-xs">
          <span className="text-muted-foreground">Total spend</span>
          <span className="tabular-nums font-medium" title="All dashboard-chat spend on this Boo">
            {formatCost(totalSpend)}
          </span>
        </div>
      )}
    </aside>
  );
}

export function BrainChatTab() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { orgId?: string; containerId?: string };
  const orgId = params.orgId!;
  const containerId = params.containerId!;
  const { thread: selectedKey, costs: costsParam } = useSearch({ strict: false }) as {
    thread?: string;
    costs?: unknown;
  };
  // Only the literal `costs=true` counts; a non-strict useSearch can hand back
  // the raw parsed value (e.g. the number 1), which must not leak into
  // react-query's `enabled`.
  const showCosts = costsParam === true;
  // Carried on every in-tab navigation so the diagnostic view survives
  // switching conversations.
  const costsSearch = showCosts ? { costs: true as const } : {};
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [freshKey, setFreshKey] = useState(newThreadKey);
  const [pendingPrompt, setPendingPrompt] = useState<{
    threadKey: string;
    text: string;
    createdAt: number;
    attachments?: { name: string; mimeType: string; size: number }[];
  } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const activeKey = selectedKey ?? freshKey;

  const { data: listResponse } = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const container = listResponse?.data.find((c) => c.id === containerId);
  const containerRunning = container?.deploymentStatus === "running";

  const threadsQ = useQuery({
    queryKey: ["brain-chat-threads", containerId],
    queryFn: () => api.containers.dashboardChat.threads(containerId),
    enabled: !!containerId && containerRunning,
    refetchInterval: 6000,
    // Replies land asynchronously — keep polling even if the operator tabs
    // away while waiting, otherwise the transcript stalls until they return.
    refetchIntervalInBackground: true,
  });
  const threads = threadsQ.data?.threads ?? [];

  const messagesQ = useQuery({
    queryKey: ["brain-chat-messages", containerId, activeKey],
    queryFn: () => api.containers.dashboardChat.messages(containerId, activeKey),
    enabled: !!containerId && containerRunning && !threadsQ.isError,
    // Poll fast only while a reply is due; replies land asynchronously.
    refetchInterval: (query) => {
      const msgs = query.state.data?.messages;
      const last = msgs?.[msgs.length - 1];
      // Fast while a reply is due or an approval is pending (the decision
      // wakes the turn and the next reply lands shortly after).
      return last?.role === "user" || last?.escalation?.status === "pending" ? 1500 : 6000;
    },
    refetchIntervalInBackground: true,
  });
  const messages = useMemo(() => messagesQ.data?.messages ?? [], [messagesQ.data]);
  const status = messagesQ.data?.status ?? null;

  // Per-session spend (LLM + services), keyed by threadKey. Cost lands after a
  // turn's LLM calls settle, so poll while the tab is open. Only fetched and
  // shown with `?costs=true` (the tab itself is super-admin gated).
  const costsQ = useQuery({
    queryKey: ["brain-chat-costs", containerId],
    queryFn: () => api.containers.dashboardChat.costs(containerId),
    enabled: !!containerId && containerRunning && showCosts,
    refetchInterval: 8000,
    refetchIntervalInBackground: true,
  });
  const costs = showCosts ? (costsQ.data?.costs ?? {}) : {};

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const sendMessage = useMutation({
    mutationFn: ({ text, voice, files }: { text: string; voice?: boolean; files?: File[] }) =>
      api.containers.dashboardChat.send(containerId, activeKey, text, { voice, files }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brain-chat-messages", containerId, activeKey] });
      queryClient.invalidateQueries({ queryKey: ["brain-chat-threads", containerId] });
      if (showCosts) queryClient.invalidateQueries({ queryKey: ["brain-chat-costs", containerId] });
      if (!selectedKey) {
        navigate({
          to: "/orgs/$orgId/containers/$containerId/chat",
          params: { orgId, containerId },
          search: { thread: activeKey, ...costsSearch },
        });
      }
    },
  });

  const stopTurn = useMutation({
    mutationFn: () => api.containers.dashboardChat.abort(containerId, activeKey),
    onSettled: () => {
      setPendingPrompt(null);
      queryClient.invalidateQueries({ queryKey: ["brain-chat-messages", containerId, activeKey] });
    },
  });

  const decideEscalation = useCallback(
    async (escalationId: string, decision: BrainChatDecision) => {
      await api.containers.dashboardChat.decideEscalation(containerId, activeKey, escalationId, decision);
      await queryClient.invalidateQueries({ queryKey: ["brain-chat-messages", containerId, activeKey] });
    },
    [activeKey, containerId, queryClient],
  );

  function selectThread(threadKey: string) {
    navigate({
      to: "/orgs/$orgId/containers/$containerId/chat",
      params: { orgId, containerId },
      search: { thread: threadKey, ...costsSearch },
    });
  }

  function newConversation() {
    setFreshKey(newThreadKey());
    setPendingPrompt(null);
    setVoiceOpen(false);
    navigate({
      to: "/orgs/$orgId/containers/$containerId/chat",
      params: { orgId, containerId },
      search: { ...costsSearch },
    });
  }

  function addFiles(incoming: FileList | File[] | null) {
    if (!incoming) return;
    const next = [...files];
    for (const file of Array.from(incoming)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setAttachError(`${file.name} is over 10 MB.`);
        continue;
      }
      if (next.length >= MAX_ATTACHMENTS) {
        setAttachError(`At most ${MAX_ATTACHMENTS} attachments per message.`);
        break;
      }
      next.push(file);
    }
    setFiles(next);
  }

  // Dropping files anywhere in the chat view attaches them, same as the paperclip.
  const dragging = usePageFileDrop({
    enabled: containerRunning && !voiceOpen && !threadsQ.isError && !sendMessage.isPending,
    onFiles: addFiles,
  });

  function send() {
    if ((!draft.trim() && files.length === 0) || sendMessage.isPending || !containerRunning) return;
    const text = draft.trim();
    const attached = files;
    setDraft("");
    setFiles([]);
    setAttachError(null);
    setPendingPrompt({
      threadKey: activeKey,
      text,
      createdAt: Date.now(),
      ...(attached.length > 0 ? { attachments: attached.map((f) => ({ name: f.name, mimeType: f.type, size: f.size })) } : {}),
    });
    sendMessage.mutate({ text, files: attached });
  }

  // Voice mode sends the transcript as a normal turn; the overlay watches the
  // same polled transcript for the reply and reads it aloud.
  const sendVoice = useCallback(
    (text: string) => {
      setPendingPrompt({ threadKey: activeKey, text, createdAt: Date.now() });
      return sendMessage.mutateAsync({ text, voice: true });
    },
    [activeKey, sendMessage],
  );

  const optimisticVisible =
    !!pendingPrompt &&
    pendingPrompt.threadKey === activeKey &&
    !messages.some((m) => m.role === "user" && m.text === pendingPrompt.text);
  const lastMessage = messages[messages.length - 1];
  const awaitingReply =
    sendMessage.isPending || optimisticVisible || (lastMessage?.role === "user" && lastMessage.kind === undefined);

  if (!containerRunning) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
        {container?.deploymentStatus === "restarting"
          ? "This Boo is restarting. Chat is back in a moment."
          : "This Boo is offline. Start it to chat."}
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 p-6 gap-4">
      <FileDropOverlay
        show={dragging}
        icon={Paperclip}
        label="Drop to attach files"
        hint={`Up to ${MAX_ATTACHMENTS} files, 10 MB each`}
      />
      <ConversationList
        threads={threads}
        costs={costs}
        isLoading={threadsQ.isLoading}
        activeKey={activeKey}
        onSelect={selectThread}
        onNew={newConversation}
      />

      <div className="relative flex-1 flex flex-col min-h-0 rounded-lg bg-card ring-1 ring-border shadow-sm overflow-hidden">
        {voiceOpen && !threadsQ.isError && (
          <Suspense
            fallback={
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/95 text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
              </div>
            }
          >
            <VoiceMode
              containerId={containerId}
              threadKey={activeKey}
              messages={messages}
              send={sendVoice}
              onClose={() => setVoiceOpen(false)}
            />
          </Suspense>
        )}
        {threadsQ.isError ? (
          <div className="flex flex-1 items-center justify-center p-6">
            <InlineError error={threadsQ.error} />
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto p-6">
              <div className="mx-auto flex max-w-3xl flex-col gap-3">
                {messagesQ.isLoading ? (
                  <div className="py-16 flex items-center justify-center text-muted-foreground">
                    <Loader2 className="size-5 animate-spin" />
                  </div>
                ) : messagesQ.isError ? (
                  <div className="py-16 flex justify-center">
                    <InlineError error={messagesQ.error} />
                  </div>
                ) : messages.length === 0 && !optimisticVisible ? (
                  <div className="py-16 text-center">
                    <p className="text-lg font-normal tracking-tight text-foreground/40 max-w-2xl mx-auto leading-snug">
                      Message {container ? "this Boo" : "your Boo"} directly from the dashboard — it replies here, in its own persistent workspace.
                    </p>
                  </div>
                ) : null}
                {messages.map((m) => (
                  <MessageBubble key={m.id} message={m} onDecide={decideEscalation} />
                ))}
                {optimisticVisible && pendingPrompt && (
                  <MessageBubble
                    message={{
                      id: `pending-${pendingPrompt.createdAt}`,
                      role: "user",
                      text: pendingPrompt.text,
                      createdAt: pendingPrompt.createdAt,
                      ...(pendingPrompt.attachments ? { attachments: pendingPrompt.attachments } : {}),
                    }}
                    onDecide={decideEscalation}
                  />
                )}
                {awaitingReply && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BooLogo className="size-4 shrink-0" />
                      <span>
                        Thinking…
                        {status && <span className="ml-1 text-foreground/70">{status}</span>}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 gap-1 px-2 text-xs"
                        disabled={stopTurn.isPending || sendMessage.isPending}
                        onClick={() => stopTurn.mutate()}
                        title="Stop this turn"
                      >
                        {stopTurn.isPending ? <Loader2 className="size-3 animate-spin" /> : <Square className="size-3" />}
                        Stop
                      </Button>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            </div>
            <div className="border-t p-4">
              <div className="mx-auto max-w-3xl">
                {sendMessage.isError && (
                  <div className="mb-2">
                    <InlineError error={sendMessage.error} />
                  </div>
                )}
                {(files.length > 0 || attachError) && (
                  <div className="mb-2 flex flex-wrap items-center gap-1.5">
                    {files.map((f, i) => (
                      <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs">
                        <Paperclip className="size-3" />
                        {f.name}
                        <span className="text-muted-foreground">{formatBytes(f.size)}</span>
                        <button type="button" aria-label={`Remove ${f.name}`} className="ml-1 rounded hover:bg-background" onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                    {attachError && <span className="text-xs text-destructive">{attachError}</span>}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sendMessage.isPending}
                    title="Attach files (or paste / drop them)"
                    aria-label="Attach files"
                  >
                    <Paperclip className="size-4" />
                  </Button>
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onPaste={(e) => {
                      const pasted = Array.from(e.clipboardData.files);
                      if (pasted.length > 0) {
                        e.preventDefault();
                        addFiles(pasted);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder="Message your Boo…"
                    rows={1}
                    disabled={sendMessage.isPending}
                    className="resize-none"
                  />
                  <Button onClick={send} disabled={sendMessage.isPending || (!draft.trim() && files.length === 0)}>
                    {sendMessage.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setVoiceOpen(true)}
                    disabled={sendMessage.isPending}
                    title="Talk to your Boo (voice mode)"
                    aria-label="Start voice mode"
                  >
                    <AudioLines className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
